{
const oaAnimalAliases = new Map([
  ['Cats', ['cat', 'cats', 'kitten', 'kittens']],
  ['Dogs', ['dog', 'dogs', 'puppy', 'puppies']],
  ['Mice', ['mouse', 'mice']],
  ['Bunnies', ['bunny', 'bunnies', 'rabbit', 'rabbits']],
  ['Birds', ['bird', 'birds']],
  ['pig', ['pig', 'pigs', 'piglet', 'piglets']]
].flatMap(([tag, words]) => words.map(word => [word, tag])));
const oaScopeSearch = (raw, availableTags, force = false) => {
  const base = raw.trim();
  const tokens = base.match(/(?:-?[a-z_][\w.]*:)?(?:"(?:\\.|[^"\\])*"|[^\s()]+)/gi) || [];
  const animalTag = token => {
    const tag = oaAnimalAliases.get(token.toLowerCase());
    return tag && availableTags.has(tag) ? tag : '';
  };
  if (!force && (/[():]/.test(base) || !tokens.some(animalTag))) return base;
  const fields = ['title', 'body', 'tag', 'product_type', 'variants.title', 'vendor', 'variants.sku', 'variants.barcode'];
  return base.replace(/(?:-?[a-z_][\w.]*:)?(?:"(?:\\.|[^"\\])*"|[^\s()]+)/gi, token => {
    if (/^(AND|OR|NOT|\*)$/.test(token) || /^-?[a-z_][\w.]*:/i.test(token)) return token;
    if (/^with$/i.test(token)) return token;
    const tag = animalTag(token);
    if (tag) return `(title:${token} OR tag:${JSON.stringify(tag)} OR variants.title:${token})`;
    const negative = token.startsWith('-');
    const value = negative ? token.slice(1) : token;
    return (negative ? '-' : '') + '(' + fields.map(field => field + ':' + value).join(' OR ') + ')';
  });
};
(() => {
  'use strict';
  if (customElements.get('oa-search')) return;
  const cache = new Map();
  class OASearch extends HTMLElement {
    static scopeSearch(raw, availableTags, force = false) { return oaScopeSearch(raw, availableTags, force); }
    connectedCallback() {
      if (this.life) return;
      this.life = new AbortController();
      const on = (el, type, fn) => el?.addEventListener(type, fn, { signal: this.life.signal });
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-oa-panel]');
      this.results = this.querySelector('[data-oa-results]');
      this.start = this.querySelector('[data-oa-start]');
      this.status = this.querySelector('[data-oa-status]');
      this.clear = this.querySelector('[data-oa-clear]');
      this.screen = this.querySelector('[data-oa-screen]');
      this.header = this.dataset.context === 'header';
      this.animalTags = new Set((this.dataset.categoryChoices || '').split(';').map(choice => choice.split('|').pop().trim()).filter(Boolean));
      if (this.header) { this.inert = true; this.setAttribute('aria-hidden', 'true'); }
      this.sequence = 0;
      this.index = -1;
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-controls', this.results.id);
      this.input.setAttribute('aria-expanded', 'false');
      this.clear.hidden = !this.input.value;
      on(this.input, 'input', e => { if (!e.isComposing) this.change(); });
      on(this.input, 'compositionend', () => this.change());
      on(this.input, 'focus', () => {
        if (this.suppressFocus || (this.header && !this.active)) return;
        if (this.input.value.trim()) this.change();
        else this.showStart();
      });
      on(this.input, 'click', () => { if (this.panel.hidden && (!this.header || this.active)) this.change(); });
      on(this, 'keydown', e => this.keys(e));
      on(this.clear, 'click', () => { this.input.value = ''; this.change(); this.input.focus(); });
      on(this.querySelector('[data-oa-close]'), 'click', () => this.close());
      on(this.querySelector('[data-oa-dismiss]'), 'click', () => { this.close(); if (this.header) return; this.suppressFocus = true; this.input.focus({ preventScroll: true }); this.suppressFocus = false; });
      on(this.screen, 'click', () => this.close());
      on(this.querySelector('form'), 'submit', event => {
        this.cancel();
        const form = event.currentTarget;
        const raw = this.input.value.trim();
        const query = form.elements.type.value === 'product' ? oaScopeSearch(raw, this.animalTags) : raw;
        if (query === raw) return;
        event.preventDefault();
        const url = new URL(form.action, location.origin);
        url.search = new URLSearchParams(new FormData(form));
        url.searchParams.set('q', query);
        location.assign(url.href);
      });
      document.addEventListener('click', e => {
        const opener = e.target.closest('.js-search-header');
        if (this.header && opener) this.returnFocus = opener;
        if (e.target.closest('.js-drawer-open-collection-filters') || (!this.header && !this.contains(e.target))) this.close(false);
      }, { capture: true, signal: this.life.signal });
      on(document, 'keyup', e => { if (e.key === 'Tab' && !this.header && !this.contains(document.activeElement)) this.close(false); });
      on(document, 'predictive-search:open', e => {
        if (!this.header || e.detail?.context !== 'header') return;
        if (!this.returnFocus?.isConnected || this.contains(this.returnFocus)) {
          this.returnFocus = this.contains(document.activeElement) ? [...document.querySelectorAll('.js-search-header')].find(el => el.getClientRects().length) : document.activeElement;
        }
        this.active = true;
        this.inert = false;
        this.setAttribute('aria-hidden', 'false');
        this.screen.hidden = false;
        document.body.classList.add('predictive-overflow-hidden');
        this.showStart();
        requestAnimationFrame(() => this.input.focus());
        if (this.input.value.trim()) this.change();
      });
      on(document, 'predictive-search:close', () => this.close(false));
    }
    disconnectedCallback() {
      this.cancel();
      this.life?.abort();
      this.life = null;
      if (this.header && this.active) this.close(false);
    }
    cancel() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.sequence++;
    }
    resetSelection() {
      this.index = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.results.querySelectorAll('[aria-selected="true"]').forEach(el => el.setAttribute('aria-selected', 'false'));
    }
    showStart() {
      this.panel.hidden = false;
      this.start.hidden = false;
      this.results.hidden = true;
      this.status.textContent = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.resetSelection();
    }
    change() {
      this.cancel();
      this.clear.hidden = !this.input.value;
      const q = this.input.value.trim();
      if (!q) { this.showStart(); return; }
      const types = this.querySelector('[name="type"]').value;
      if (this.dataset.enabled === 'false' || !types.includes('product')) {
        this.panel.hidden = true;
        return;
      }
      this.panel.hidden = false;
      this.start.hidden = true;
      this.results.hidden = true;
      this.resetSelection();
      this.input.setAttribute('aria-expanded', 'false');
      this.status.textContent = this.dataset.loading;
      this.status.classList.remove('visually-hidden');
      const seq = this.sequence;
      this.timer = setTimeout(() => this.search(q, seq), 180);
    }
    async search(q, seq) {
      const url = new URL(this.dataset.endpoint, location.origin);
      url.search = new URLSearchParams({ q: oaScopeSearch(q, this.animalTags), 'resources[type]': 'product', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last', section_id: 'oa-search-results' });
      const key = url.href;
      const hit = cache.get(key);
      if (hit && Date.now() - hit.at < 120000) { this.render(hit.html, seq); return; }
      this.request = new AbortController();
      const request = this.request;
      const timeout = setTimeout(() => request.abort(), 8000);
      try {
        const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Search unavailable');
        const html = await response.text();
        const section = new DOMParser().parseFromString(html, 'text/html').getElementById('shopify-section-oa-search-results');
        if (!section) throw new Error('Missing search section');
        if (seq !== this.sequence) return;
        cache.delete(key);
        cache.set(key, { html: section.innerHTML, at: Date.now() });
        if (cache.size > 20) cache.delete(cache.keys().next().value);
        this.render(section.innerHTML, seq);
      } catch (_) {
        if (seq !== this.sequence) return;
        this.status.textContent = this.dataset.error;
        this.status.classList.remove('visually-hidden');
        this.results.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
      } finally { clearTimeout(timeout); }
    }
    render(html, seq) {
      if (seq !== this.sequence || !this.isConnected) return;
      this.results.innerHTML = html;
      this.results.querySelectorAll('[role="option"]').forEach((el, i) => { el.id = `${this.input.id}-option-${i}`; });
      this.panel.hidden = false;
      this.results.hidden = false;
      this.start.hidden = true;
      this.input.setAttribute('aria-expanded', 'true');
      this.status.textContent = this.dataset.ready;
      this.status.classList.add('visually-hidden');
    }
    close(restore = true) {
      this.cancel();
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.resetSelection();
      if (this.header) { this.inert = true; this.setAttribute('aria-hidden', 'true'); }
      if (this.header && this.active) {
        this.active = false;
        this.screen.hidden = true;
        document.body.classList.remove('predictive-overflow-hidden');
        document.dispatchEvent(new CustomEvent('predictive-search:close-all'));
        if (restore && this.returnFocus?.isConnected) this.returnFocus.focus();
      }
    }
    keys(e) {
      if (e.isComposing) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.target === this.input && !this.results.hidden && !this.panel.hidden) {
        const options = [...this.results.querySelectorAll('[role="option"]')];
        if (!options.length) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.index = this.index < 0 ? (e.key === 'ArrowDown' ? 0 : options.length - 1) : (this.index + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
          options.forEach((el, i) => el.setAttribute('aria-selected', String(i === this.index)));
          const selected = options[this.index];
          this.input.setAttribute('aria-activedescendant', selected.id);
          selected.scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter' && this.index >= 0) { e.preventDefault(); options[this.index].click(); }
      }
      if (e.key === 'Tab' && this.header && this.active) {
        const focusable = [...this.querySelectorAll('a[href],button,input:not([type="hidden"]),summary')].filter(el => !el.disabled && el.getClientRects().length);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
  }
  customElements.define('oa-search', OASearch);
})();

}

